import * as React from 'react';
import {withI18n, WithI18nProps} from '@shopify/react-i18n';
import {Category, Shop} from 'interfaces';
import {Breadcrumb, ContentBlock} from 'components/UIKit';
import {
  Hero,
  SocialProof,
  WhyExchange,
  HowExchange,
  InfoBar,
  ListingLayout,
  Support,
  SubscriptionLayout,
  CategoryCardLayout,
} from 'components/Marketplace';
import styles from '../Location.scss';
import {fallbackSubscriptionPictureData} from '../images';
import * as locales from './locales';

export interface LocationComponentProps {
  authenticityToken: string;
  location: Category;
  locationDefiniteArticle: string;
  breadcrumbs: Breadcrumb[];
  type: string;
  locationsShopPath: string;
  createAListingPath: string;
  shopsPath: string;
  topGrowingLocations: Category[];
  similarLocations: Category[];
  bestListings: Shop[];
  soldListings: Shop[];
  soldListingsPath: string;
}

function LocationComponent(props: LocationComponentProps & WithI18nProps) {
  const {
    authenticityToken,
    location,
    locationDefiniteArticle,
    breadcrumbs,
    type,
    locationsShopPath,
    shopsPath,
    topGrowingLocations = [],
    similarLocations = [],
    bestListings = [],
    soldListings = [],
    soldListingsPath,
    i18n,
  } = props;

  const {seoData, secondarySeoData} = location;
  const locationTypeTranslationKey = type === 'country' ? 'country' : 'children';
  const similarLocationsPluralizedType = type === 'city' ? 'cities' : 'regions';
  const topGrowingLocationType = type === 'country' ? 'regions' : 'cities';
  const similarLocationsHeadingReplacement =
    type === 'country'
      ? locationDefiniteArticle
      : i18n.translate(`locationTypes.${similarLocationsPluralizedType}`);

  const showSeoContent = Boolean(seoData.content && seoData.heading);
  const showSecondSeoContent = Boolean(secondarySeoData.content && secondarySeoData.heading);
  const showTopGrowingLocations = Boolean(topGrowingLocations.length);
  const showSimilarLocations = Boolean(similarLocations.length);
  const showBestListings = Boolean(bestListings.length);
  const showSoldListings = Boolean(soldListings.length);

  return (
    <>
      <InfoBar breadcrumbs={breadcrumbs} />
      <Hero
        heading={location.heading || i18n.translate('heading', {location: locationDefiniteArticle})}
        subhead={location.subhead || i18n.translate('subhead')}
        cta={i18n.translate('cta', {location: locationDefiniteArticle})}
        url={locationsShopPath}
        imageUrl={location.imageUrl}
      />
      {showSeoContent && (
        <ContentBlock heading={seoData.heading} subhead={seoData.subhead} copy={seoData.content} />
      )}
      {showTopGrowingLocations && (
        <CategoryCardLayout
          heading={i18n.translate('topGrowingLocations.heading', {
            locationType: i18n.translate(`locationTypes.${topGrowingLocationType}`),
            countryName: locationDefiniteArticle,
          })}
          subhead={i18n.translate('topGrowingLocations.subhead', {
            countryName: locationDefiniteArticle,
            locationType: i18n.translate(`locationTypes.${topGrowingLocationType}`),
          })}
          categories={topGrowingLocations || []}
          className={styles.BackgroundSilverLight}
        />
      )}
      {type !== 'city' && <WhyExchange />}
      {showBestListings && (
        <ListingLayout
          heading={i18n.translate('bestListings.heading', {location: locationDefiniteArticle})}
          subhead={i18n.translate('bestListings.subhead', {location: locationDefiniteArticle})}
          shops={bestListings || []}
          cta={i18n.translate('bestListings.cta')}
          ctaPath={locationsShopPath}
          className={styles.BackgroundSilverLight}
        />
      )}
      {type === 'city' && <WhyExchange />}
      {showSecondSeoContent && (
        <ContentBlock
          heading={secondarySeoData.heading}
          subhead={secondarySeoData.subhead}
          copy={secondarySeoData.content}
          theme="purpleDark"
        />
      )}
      {showSimilarLocations && (
        <CategoryCardLayout
          heading={i18n.translate(`similarLocations.${locationTypeTranslationKey}.heading`, {
            qualifier: similarLocationsHeadingReplacement,
          })}
          subhead={i18n.translate(`similarLocations.${locationTypeTranslationKey}.subhead`, {
            qualifier: similarLocationsHeadingReplacement,
          })}
          categories={similarLocations || []}
          className={styles.BackgroundSilverLight}
        />
      )}
      <SocialProof ctaPath={shopsPath} />
      <SubscriptionLayout
        authenticityToken={authenticityToken}
        subhead={i18n.translate('subscription.subhead')}
        imageUrl={location.secondaryImageUrl}
        fallbackPictureData={fallbackSubscriptionPictureData}
      />
      {showSoldListings && (
        <ListingLayout
          heading={i18n.translate('soldListings.heading')}
          subhead={i18n.translate('soldListings.subhead')}
          shops={soldListings || []}
          cta={i18n.translate('soldListings.cta')}
          ctaPath={soldListingsPath}
        />
      )}
      <HowExchange />
      <Support />
    </>
  );
}

export const Location = withI18n({
  id: 'Location',
  fallback: locales.en,
})(LocationComponent);
